import { FileMetadata } from '@/models/registry';
import type { FileMetadata as FileMetadataType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

export class FileMetadataStore
  implements BaseStore<FileMetadata, FileMetadataType>
{
  store: Map<string, FileMetadata> = new Map();

  storeIndexedByForeignKey: Map<string, FileMetadata[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setFileField = <K extends keyof FileMetadataType>(
    file: FileMetadata,
    key: K,
    value: FileMetadataType[K],
  ) => {
    (file as Pick<FileMetadata, K>)[key] = value as FileMetadata[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<FileMetadataType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_files.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_files.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_files.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_files.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<FileMetadataType>;
  }) {
    const existingFile = this.getById(delta.record_id);
    if (!existingFile) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof FileMetadataType,
      FileMetadataType[keyof FileMetadataType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingFile[key] === newValue) {
        continue;
      }

      this.setFileField(existingFile, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingFile);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<FileMetadataType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<FileMetadataType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_files.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.remove(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): FileMetadata | undefined {
    return this.store.get(id);
  }

  getByDealId(dealId: string): FileMetadata[] {
    return this.storeIndexedByForeignKey.get(dealId) || [];
  }

  add(input: FileMetadataType) {
    const fileMetadata = new FileMetadata(input);
    this.store.set(fileMetadata.id, fileMetadata);
    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(fileMetadata.deal_id) || [];
    arr.push(fileMetadata);
    this.storeIndexedByForeignKey.set(fileMetadata.deal_id, arr);
  }

  // Computed getters - automatically update when store changes
  get all() {
    return Array.from(this.store.values());
  }

  upsert(fileMetadata: FileMetadataType) {
    const existingFile = this.getById(fileMetadata.id);
    if (existingFile) {
      this.replace({ id: fileMetadata.id, fileMetadata });
    } else {
      this.add(fileMetadata);
    }
  }

  replace({
    id,
    fileMetadata,
  }: {
    id: string;
    fileMetadata: FileMetadataType;
  }) {
    const existingFile = this.getById(id);
    if (!existingFile) return;
    existingFile.name = fileMetadata.name;
    existingFile.url = fileMetadata.url;
    existingFile.type = fileMetadata.type;
    existingFile.size = fileMetadata.size;
    this.save(existingFile);
  }

  save(file: FileMetadata) {
    this.store.set(file.id, file);
    // update the storeIndexedByForeignKey
    const arr = this.storeIndexedByForeignKey.get(file.deal_id) || [];
    const index = arr.findIndex((f) => f.id === file.id);
    if (index !== -1) {
      arr[index] = file;
    }
    this.storeIndexedByForeignKey.set(file.deal_id, arr);
  }

  update(file: FileMetadata) {
    this.store.set(file.id, file);

    // update the storeIndexedByForeignKey
    const arr = this.storeIndexedByForeignKey.get(file.deal_id) || [];
    const index = arr.findIndex((f) => f.id === file.id);
    if (index !== -1) {
      arr[index] = file;
    }
    this.storeIndexedByForeignKey.set(file.deal_id, arr);
  }

  updateMany(files: FileMetadata[]) {
    files.forEach((file) => this.update(file));
  }

  updateName(id: FileMetadataType['id'], name: FileMetadataType['name']) {
    const file = this.getById(id);
    if (!file) return;
    file.name = name;
    this.update(file);
  }

  remove(fileId: FileMetadataType['id']) {
    const file = this.getById(fileId);
    if (!file) return;

    this.store.delete(fileId);

    // remove from the index
    const arr = this.storeIndexedByForeignKey.get(file.deal_id) || [];
    this.storeIndexedByForeignKey.set(
      file.deal_id,
      arr.filter((f) => f.id !== fileId),
    );

    // remove from dexie
    db.deal_files.delete(fileId);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_files',
      entity: 'deal_files',
      modelStore: fileMetadataStore,
    });
  }

  async hydrateDeal(dealId: string): Promise<void> {
    const files = await db.deal_files.where('deal_id').equals(dealId).toArray();
    files.forEach((file) => this.upsert(file));
  }
}

export const fileMetadataStore = new FileMetadataStore();
